import React, { useEffect, useMemo, useState } from 'react';
import { Column } from '@tanstack/react-table';
import { Check, X } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '../../ui/command';
import { Slider } from '../../ui/slider';
import { cn } from '../../../lib/utils';

// The three editors a filter chip can open, chosen by column.meta.filter.
// Each edits column.getFilterValue() directly; facet options and range
// bounds come from TanStack's faceted row model so they respect other filters.

const fieldCls = 'h-8 w-full rounded-md border border-[color:var(--oa-line-2)] px-2 text-[13px] tabular-nums text-[color:var(--oa-text)] outline-none focus:border-[color:var(--oa-accent)]';

export function FacetEditor<T>({ column, onDone }: { column: Column<T, unknown>; onDone: () => void }) {
  const selected = new Set((column.getFilterValue() as string[] | undefined) ?? []);
  const facets = column.getFacetedUniqueValues();
  // Array-valued columns (categories) come back keyed by the whole array;
  // flatten so each tag is its own option with a summed count.
  const options = useMemo(() => {
    const counts = new Map<string, number>();
    facets.forEach((n, value) => {
      // SKU sub-rows answer undefined for style columns; skip them.
      if (value === undefined || value === null || value === '') return;
      const values = Array.isArray(value) ? value : [value];
      values.forEach((v) => {
        if (v === undefined || v === null || v === '') return;
        const key = String(v);
        counts.set(key, (counts.get(key) ?? 0) + n);
      });
    });
    const list = Array.from(counts.entries()).map(([value, n]) => ({ value, n }));
    list.sort((a, b) => b.n - a.n || a.value.localeCompare(b.value));
    return list;
  }, [facets]);
  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    column.setFilterValue(next.size ? Array.from(next) : undefined);
  };
  const label = (column.columnDef.meta?.label ?? column.id).toLowerCase();
  return (
    <Command>
      <CommandInput autoFocus placeholder={`Search ${label}…`} className="text-[13px]" />
      <CommandList className="max-h-[320px]">
        <CommandEmpty className="py-6 text-center text-[13px] text-[color:var(--oa-muted)]">Nothing matches.</CommandEmpty>
        <CommandGroup>
          {options.map((o) => {
            const on = selected.has(o.value);
            return (
              <CommandItem key={o.value} value={o.value} onSelect={() => toggle(o.value)} className="gap-2 text-[13px]">
                <span className={cn('flex h-4 w-4 items-center justify-center rounded border', on ? 'border-[color:var(--oa-accent)] bg-[color:var(--oa-accent)] text-[color:var(--oa-on-accent)]' : 'border-[color:var(--oa-line-3)] bg-[color:var(--oa-card)]')}>
                  {on ? <Check className="h-3 w-3" /> : null}
                </span>
                <span className="flex-1 truncate">{o.value || '—'}</span>
                <span className="tabular-nums text-[11px] text-[color:var(--oa-muted)]">{o.n}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup>
          <div className="flex items-center justify-between px-2 py-1.5 text-[12px]">
            <button type="button" onClick={() => column.setFilterValue(undefined)} className="inline-flex items-center gap-1 text-[color:var(--oa-muted)] hover:text-[color:var(--oa-text)]">
              <X className="h-3.5 w-3.5" /> Clear
            </button>
            <button type="button" onClick={onDone} className="rounded-md bg-[color:var(--oa-pill-bg)] px-2.5 py-1 font-semibold text-[color:var(--oa-pill-text)] hover:bg-[color:color-mix(in_oklab,var(--oa-pill-bg)_86%,var(--oa-card))]">
              Done{selected.size ? ` (${selected.size})` : ''}
            </button>
          </div>
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

export function RangeEditor<T>({ column, onDone }: { column: Column<T, unknown>; onDone: () => void }) {
  const meta = column.columnDef.meta;
  const format = meta?.format ?? ((v: number) => String(v));
  const step = meta?.step ?? 1;
  const bounds = column.getFacetedMinMaxValues();
  const lo = bounds ? Math.floor(bounds[0]) : 0;
  const hi = bounds ? Math.ceil(bounds[1]) : 0;
  const current = column.getFilterValue() as [number | undefined, number | undefined] | undefined;
  const [draft, setDraft] = useState<[number, number]>([current?.[0] ?? lo, current?.[1] ?? hi]);
  useEffect(() => setDraft([current?.[0] ?? lo, current?.[1] ?? hi]), [lo, hi]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => {
    const [a, b] = draft;
    column.setFilterValue(a <= lo && b >= hi ? undefined : [a, b]);
    onDone();
  };

  return (
    <div className="w-[280px] p-3">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-[color:var(--oa-text)]">{meta?.label}</span>
        <span className="text-[12px] tabular-nums text-[color:var(--oa-muted)]">{format(draft[0])} – {format(draft[1])}</span>
      </div>
      <Slider
        min={lo}
        max={hi}
        step={step}
        value={draft}
        onValueChange={(v) => setDraft([v[0], v[1]] as [number, number])}
        className="[&_[role=slider]]:border-[color:var(--oa-accent)] [&_[role=slider]]:bg-[color:var(--oa-card)] [&_.bg-primary]:bg-[color:var(--oa-accent)]"
      />
      <div className="mt-3 grid grid-cols-2 gap-2">
        <label className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--oa-muted)]">
          Min
          <input type="number" autoFocus value={draft[0]} min={lo} max={draft[1]} step={step} onChange={(e) => setDraft([Math.max(lo, Math.min(Number(e.target.value), draft[1])), draft[1]])} onKeyDown={(e) => e.key === 'Enter' && apply()} className={cn(fieldCls, 'mt-1')} />
        </label>
        <label className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--oa-muted)]">
          Max
          <input type="number" value={draft[1]} min={draft[0]} max={hi} step={step} onChange={(e) => setDraft([draft[0], Math.min(hi, Math.max(Number(e.target.value), draft[0]))])} onKeyDown={(e) => e.key === 'Enter' && apply()} className={cn(fieldCls, 'mt-1')} />
        </label>
      </div>
      <div className="mt-3 flex items-center justify-between text-[12px]">
        <button type="button" onClick={() => { column.setFilterValue(undefined); onDone(); }} className="inline-flex items-center gap-1 text-[color:var(--oa-muted)] hover:text-[color:var(--oa-text)]"><X className="h-3.5 w-3.5" /> Clear</button>
        <button type="button" onClick={apply} className="rounded-md bg-[color:var(--oa-pill-bg)] px-2.5 py-1 font-semibold text-[color:var(--oa-pill-text)] hover:bg-[color:color-mix(in_oklab,var(--oa-pill-bg)_86%,var(--oa-card))]">Apply</button>
      </div>
    </div>
  );
}

export function TextEditor<T>({ column, onDone }: { column: Column<T, unknown>; onDone: () => void }) {
  const [draft, setDraft] = useState<string>((column.getFilterValue() as string | undefined) ?? '');
  const apply = () => {
    column.setFilterValue(draft.trim() ? draft.trim() : undefined);
    onDone();
  };
  return (
    <div className="w-[280px] p-3">
      <div className="mb-2 text-[13px] font-semibold text-[color:var(--oa-text)]">{column.columnDef.meta?.label} contains</div>
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') apply();
          if (e.key === 'Escape') onDone();
        }}
        placeholder="Type words, all must match"
        className={fieldCls}
      />
      <div className="mt-3 flex items-center justify-between text-[12px]">
        <button type="button" onClick={() => { column.setFilterValue(undefined); onDone(); }} className="inline-flex items-center gap-1 text-[color:var(--oa-muted)] hover:text-[color:var(--oa-text)]"><X className="h-3.5 w-3.5" /> Clear</button>
        <button type="button" onClick={apply} className="rounded-md bg-[color:var(--oa-pill-bg)] px-2.5 py-1 font-semibold text-[color:var(--oa-pill-text)] hover:bg-[color:color-mix(in_oklab,var(--oa-pill-bg)_86%,var(--oa-card))]">Apply</button>
      </div>
    </div>
  );
}

export function FilterEditor<T>({ column, onDone }: { column: Column<T, unknown>; onDone: () => void }) {
  const kind = column.columnDef.meta?.filter;
  if (kind === 'facet') return <FacetEditor column={column} onDone={onDone} />;
  if (kind === 'range') return <RangeEditor column={column} onDone={onDone} />;
  if (kind === 'text') return <TextEditor column={column} onDone={onDone} />;
  return null;
}

// One-line description of a column's current filter, for chips.
export function describeFilter<T>(column: Column<T, unknown>): string | null {
  const v = column.getFilterValue();
  const kind = column.columnDef.meta?.filter;
  if (v === undefined || v === null) return null;
  if (kind === 'facet') {
    const arr = v as string[];
    if (!arr.length) return null;
    return arr.length <= 2 ? arr.join(', ') : `${arr[0]}, ${arr[1]} +${arr.length - 2}`;
  }
  if (kind === 'range') {
    const [a, b] = v as [number | undefined, number | undefined];
    const f = column.columnDef.meta?.format ?? ((n: number) => String(n));
    if (a === undefined && b === undefined) return null;
    if (a !== undefined && b !== undefined) return `${f(a)} – ${f(b)}`;
    return a !== undefined ? `≥ ${f(a)}` : `≤ ${f(b as number)}`;
  }
  if (kind === 'text') return `“${String(v)}”`;
  return String(v);
}
